import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaDonate } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import ResponsiveImage from '../components/ResponsiveImage';
import ProgramSubNav from '../components/ProgramSubNav';
import { getPublicProgramme, preloadPublicProgrammes, preloadPublicProgramProjets } from '../services/siteContentService';
import { useTranslation } from 'react-i18next';
import { usePublicPaths } from '../routes/publicPaths';
import { tProgramDescription, tProgramTitle } from '../utils/publicI18n';
import { resolveProgramSubNavItems } from '../config/programSubNav';
import { scrollToPageTop, scrollToProgramSection } from '../utils/programScroll';
import { closeHeaderMenus } from '../utils/headerMenus';
import { normalizeStorageUrl, preloadImage, injectDocumentPreload } from '../config/apiBase';
import {
  getProgramHeroImage,
  mergeProgramWithCache,
  getProgramHeroAssets,
  fastestHeroUrl,
  writeProgramHeroCache,
  PRIORITY_PROGRAM_SLUGS,
} from '../config/programHeroImages';
import { readProgramProjetsCache, writeProgramProjetsCache } from '../config/programProjetsCache';
import { prefetchProgramHero, prefetchProgramProjets, prefetchProjetCardImages, PROJET_CARD_SIZES } from '../utils/prefetchProgramHero';
import KafalaImpactStats from '../components/KafalaImpactStats';
import KafalaOrphelinsWaiting from '../components/KafalaOrphelinsWaiting';
import KafalaDemandeForm from '../components/KafalaDemandeForm';
import SaqyaWellRequest from '../components/SaqyaWellRequest';
import OmraRequest from '../components/OmraRequest';
import './ProgramDetail.css';

const FALLBACK_PROGRAMS = {
  adahi: {
    title: 'أضحية الخير',
    description:
      'مشروع الأضاحي الخيرية مبادرة إنسانية موسمية تهدف إلى تمكين الأسر المحتاجة من الاستفادة من شعيرة الأضحية خلال عيد الأضحى المبارك، بما يعزز قيم التراحم والتكافل.',
  },
  ihyaa: {
    title: 'أضحية الخير',
    description:
      'مشروع الأضاحي الخيرية مبادرة إنسانية موسمية تهدف إلى تمكين الأسر المحتاجة من الاستفادة من شعيرة الأضحية خلال عيد الأضحى المبارك، بما يعزز قيم التراحم والتكافل.',
  },
  namaa: {
    title: 'نـماء',
    description: 'تنفيذ مشاريع دخل لتحسين مستوى المعيشة للأسر الفقيرة.',
  },
  kafala: {
    title: 'كفالة',
    description:
      '"أيتامنا" هو مشروع إنساني يهدف إلى توفير رعاية شاملة للأيتام، من خلال تقديم الدعم الاجتماعي والتربوي والتعليمي والصحي، بما يسهم في تحسين جودة حياتهم، وتنمية قدراتهم، وتمكينهم من بناء مستقبل أكثر استقرارًا وإشراقًا.',
  },
  saqya: {
    title: 'سقيا',
    description:
      'يأتي مشروع "سقيا عطاء" استجابةً لحاجة الساكنة إلى المياه النقية، حيث يعمل على توفير مصادر مستدامة لمياه الشرب من خلال حفر وتجهيز الآبار وتسهيل وصول المياه إلى الأسر بالمناطق والدواوير التي تعاني من ندرتها.',
  },
  ighatha: {
    title: 'إغاثة',
    description: 'حملات موسمية لتخفيف معاناة الأسر المحتاجة خلال الظروف الصعبة.',
  },
  imaar: {
    title: 'إعمار',
    description: 'إعمار؛ تربيةٌ تُزهر، وتعليمٌ يُثمر، وتنميةٌ تُبهر.',
  },
  ramadan: {
    title: 'مائدة الرحمن',
    description: 'برنامج مائدة الرحمن لإفطار الصائمين ودعم الأسر المحتاجة خلال شهر رمضان المبارك.',
  },
  omra: {
    title: 'ضيوف الرحمن',
    description:
      'يتيح برنامج خدمة ضيوف الرحمن للقادمين إلى الحرمين الشريفين من الخارج والداخل، مرافق ذات جودة عالية، تساعد الجميع على أن ينعموا بتجربة إيمانية مميزة لا تنسى.',
  },
  caravane: {
    title: 'قوافل تضامنية',
    description:
      'القافلة التضامنية مبادرة إنسانية تسعى إلى مدّ يد العون للأسر المحتاجة خلال الأزمات والظروف الصعبة، من خلال تنظيم حملات موسمية تهدف إلى توفير الاحتياجات الأساسية وتحسين ظروف العيش للفئات الأكثر هشاشة.',
  },
};

const buildFallbackProgram = (slug) =>
  FALLBACK_PROGRAMS[slug] ? { slug, ...FALLBACK_PROGRAMS[slug], image_url: null } : null;

const FACEBOOK_PLAYER_WIDTH = 768;
const FACEBOOK_PLAYER_HEIGHT = 432;

const isFacebookVideoEmbed = (src) =>
  typeof src === 'string' && src.includes('facebook.com/plugins/video.php');

const sizedFacebookVideoSrc = (src) => {
  if (!isFacebookVideoEmbed(src)) return src;
  try {
    const url = new URL(src);
    url.searchParams.set('width', String(FACEBOOK_PLAYER_WIDTH));
    url.searchParams.set('height', String(FACEBOOK_PLAYER_HEIGHT));
    url.searchParams.set('show_text', 'false');
    return url.toString();
  } catch {
    return src;
  }
};

const resolveProgramVideo = (program) => {
  const fileUrl = normalizeStorageUrl(program?.video_file_url);
  if (fileUrl) {
    return { type: 'file', src: fileUrl };
  }

  const embed = program?.video_embed_src || '';
  if (embed.includes('youtube.com/embed') || embed.includes('player.vimeo.com') || embed.includes('facebook.com/plugins/video.php')) {
    return { type: 'embed', src: embed };
  }

  const url = String(program?.video_url || '');
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  if (yt) {
    return { type: 'embed', src: `https://www.youtube.com/embed/${yt[1]}` };
  }

  if (embed.includes('instagram.com')) {
    return { type: 'instagram', src: embed };
  }

  const ig = url.match(/instagram\.com\/(?:p|reel|reels|tv)\/([A-Za-z0-9_-]+)/i);
  if (ig) {
    const kind = url.includes('/tv/') ? 'tv' : url.includes('/reel') ? 'reel' : 'p';
    return { type: 'instagram', src: `https://www.instagram.com/${kind}/${ig[1]}/embed` };
  }

  return null;
};

const normalizeProgram = (data, slug) => mergeProgramWithCache(slug, data);

const normalizeProjet = (p) => ({
  ...p,
  image_url: normalizeStorageUrl(p.image_url),
  image_url_webp: normalizeStorageUrl(p.image_url_webp),
  image_url_webp_sm: normalizeStorageUrl(p.image_url_webp_sm),
  image_url_webp_card: normalizeStorageUrl(p.image_url_webp_card),
});

const progressOf = (projet) => {
  const explicit = Number(projet?.progress_percent);
  if (Number.isFinite(explicit)) return explicit;
  const goal = Number(projet?.goal_amount);
  const collected = Number(projet?.collected_amount);
  if (!Number.isFinite(goal) || goal <= 0) return 0;
  return Math.min(100, (collected / goal) * 100);
};

const ProjetCard = ({ projet, priority = false }) => {
  const paths = usePublicPaths();
  return (
  <Link
    to={paths.projet(projet.slug)}
    className="program-projet-card"
  >
    {projet.image_url ? (
      <div className="program-projet-card__img-wrap">
        <ResponsiveImage
          src={projet.image_url}
          webpSm={projet.image_url_webp_sm}
          webpCard={projet.image_url_webp_card}
          alt={projet.title}
          className="program-projet-card__img"
          sizes={PROJET_CARD_SIZES}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'low' : 'auto'}
          decoding="async"
        />
      </div>
    ) : (
      <div className="program-projet-card__placeholder">💧</div>
    )}
    <div className="program-projet-card__body">
      <h3>{projet.title}</h3>
      {projet.region && <p className="program-projet-card__region">{projet.region}</p>}
      <div className="program-projet-card__progress">
        <div className="program-projet-card__bar">
          <div style={{ width: `${projet.progress_percent}%` }} />
        </div>
        <span>{projet.progress_percent}%</span>
      </div>
    </div>
  </Link>
  );
};

const resolveHeroAssets = (slug, locationState) => {
  const cached = getProgramHeroAssets(slug);
  return {
    image_url: getProgramHeroImage(locationState?.heroImage) || cached.image_url,
    image_url_webp: getProgramHeroImage(locationState?.heroWebp) || cached.image_url_webp,
    image_url_webp_sm: getProgramHeroImage(locationState?.heroWebpSm) || cached.image_url_webp_sm,
  };
};

const preloadHeroAssets = (assets) => {
  const fastest = fastestHeroUrl(assets);
  if (!fastest) return null;
  if (assets.image_url_webp_sm && assets.image_url_webp) {
    injectDocumentPreload(fastest, {
      imageSrcSet: `${assets.image_url_webp_sm} 960w, ${assets.image_url_webp} 1600w`,
      imageSizes: '100vw',
    });
  } else {
    injectDocumentPreload(fastest);
  }
  void preloadImage(fastest);
  return fastest;
};

const ProgramDetail = () => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const paths = usePublicPaths();
  const heroImgRef = useRef(null);
  const [program, setProgram] = useState(() => normalizeProgram(null, slug) || buildFallbackProgram(slug));
  const [projets, setProjets] = useState(() => {
    const cached = readProgramProjetsCache(slug);
    return cached ? cached.map(normalizeProjet) : [];
  });
  const [projetsLoading, setProjetsLoading] = useState(() => !readProgramProjetsCache(slug));
  const initialAssets = resolveHeroAssets(slug, location.state);
  const initialFastest = fastestHeroUrl(initialAssets);
  const [heroImageLoaded, setHeroImageLoaded] = useState(Boolean(initialFastest));
  const [heroImage, setHeroImage] = useState(() => {
    preloadHeroAssets(initialAssets);
    return initialAssets.image_url;
  });
  const [heroWebp, setHeroWebp] = useState(initialAssets.image_url_webp);
  const [heroWebpSm, setHeroWebpSm] = useState(initialAssets.image_url_webp_sm);
  const prevSlugRef = useRef(null);
  const skipHashScrollRef = useRef(false);
  const programPath = paths.programme(slug);

  const applyHeroAssets = (assets) => {
    const next = {
      image_url: getProgramHeroImage(assets?.image_url),
      image_url_webp: getProgramHeroImage(assets?.image_url_webp),
      image_url_webp_sm: getProgramHeroImage(assets?.image_url_webp_sm),
    };
    const jpeg = next.image_url || next.image_url_webp || next.image_url_webp_sm;
    preloadHeroAssets(next);
    setHeroImage(jpeg);
    setHeroWebp(next.image_url_webp);
    setHeroWebpSm(next.image_url_webp_sm);
    setHeroImageLoaded(Boolean(jpeg));
  };

  useLayoutEffect(() => {
    closeHeaderMenus();
  }, [slug]);

  useLayoutEffect(() => {
    applyHeroAssets(resolveHeroAssets(slug, location.state));
    setProgram(normalizeProgram(null, slug) || buildFallbackProgram(slug));
    const cachedProjets = readProgramProjetsCache(slug);
    const normalizedCached = cachedProjets ? cachedProjets.map(normalizeProjet) : [];
    setProjets(normalizedCached);
    setProjetsLoading(!cachedProjets);
    if (normalizedCached.length > 0) {
      const current = normalizedCached.filter((p) => progressOf(p) < 100);
      const completed = normalizedCached.filter((p) => progressOf(p) >= 100);
      prefetchProjetCardImages([...current, ...completed]);
    }
  }, [slug]);

  useEffect(() => {
    let cancelled = false;

    const loadProgram = async () => {
      try {
        const list = await preloadPublicProgrammes();
        if (cancelled) return;
        const fromList = Array.isArray(list) ? list.find((p) => p.slug === slug) : null;
        if (fromList) {
          writeProgramHeroCache(slug, fromList);
          applyHeroAssets(fromList);
          setProgram(normalizeProgram(fromList, slug));
        }

        const programData = await getPublicProgramme(slug);
        if (cancelled) return;
        if (programData?.slug && programData.slug !== slug) {
          navigate(`${paths.programme(programData.slug)}${location.hash || ''}`, {
            replace: true,
            state: location.state,
          });
          return;
        }
        const normalized = normalizeProgram(programData, slug);
        if (!normalized) return;

        writeProgramHeroCache(slug, normalized);
        applyHeroAssets(normalized);
        setProgram(normalized);
      } catch {
        if (!cancelled) {
          setProgram(normalizeProgram(null, slug) || buildFallbackProgram(slug));
        }
      }
    };

    loadProgram();

    return () => {
      cancelled = true;
    };
  }, [slug, location.hash, location.state, navigate, paths]);

  useEffect(() => {
    PRIORITY_PROGRAM_SLUGS.filter((item) => item !== slug).forEach((item) => {
      prefetchProgramHero(item);
      prefetchProgramProjets(item);
    });
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    const cached = readProgramProjetsCache(slug);
    if (cached) {
      setProjets(cached.map(normalizeProjet));
      setProjetsLoading(false);
    } else {
      setProjetsLoading(true);
      setProjets([]);
    }

    preloadPublicProgramProjets(slug)
      .then((projetsData) => {
        if (cancelled) return;
        if (!Array.isArray(projetsData)) {
          if (!cached) setProjets([]);
          return;
        }
        const list = projetsData.map(normalizeProjet);
        if (list.length > 0) {
          writeProgramProjetsCache(slug, projetsData);
        }
        setProjets(list);
        const current = list.filter((p) => progressOf(p) < 100);
        const completed = list.filter((p) => progressOf(p) >= 100);
        prefetchProjetCardImages([...current, ...completed]);
      })
      .catch(() => {
        if (!cancelled && !cached) setProjets([]);
      })
      .finally(() => {
        if (!cancelled) setProjetsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  useLayoutEffect(() => {
    const img = heroImgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setHeroImageLoaded(true);
    }
  }, [heroImage]);

  useLayoutEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useLayoutEffect(() => {
    if (prevSlugRef.current === slug) return;

    prevSlugRef.current = slug;
    skipHashScrollRef.current = true;
    scrollToPageTop('auto');

    if (location.hash) {
      navigate(programPath, { replace: true });
    }
  }, [slug, programPath, navigate, location.hash]);

  useEffect(() => {
    if (skipHashScrollRef.current) {
      skipHashScrollRef.current = false;
      return;
    }

    if (!program || !location.hash || location.pathname !== programPath) return;

    const id = location.hash.replace('#', '');
    const timer = window.setTimeout(() => {
      scrollToProgramSection(id);
    }, 120);

    return () => window.clearTimeout(timer);
  }, [location.hash, location.pathname, program, programPath, slug]);

  const currentProjets = projets.filter((p) => progressOf(p) < 100);
  const completedProjets = projets.filter((p) => progressOf(p) >= 100);

  if (!program) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center p-4">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('public.program.notFound')}</h2>
        <Link to={paths.programmes} className="text-brand-primary hover:underline">
          {t('public.program.backToList')}
        </Link>
      </div>
    );
  }

  const heroIsReady = Boolean(heroImage && heroImageLoaded);
  const video = resolveProgramVideo(program);
  const isKafala = slug === 'kafala' || program.slug === 'kafala';
  const isSaqya = slug === 'saqya' || program.slug === 'saqya';
  const isOmra = slug === 'omra' || program.slug === 'omra';

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{`${tProgramTitle(t, slug, program.title)} | ${t('public.brand.titleSuffix')}`}</title>
        <meta name="description" content={(tProgramDescription(t, slug, program.description) || '').slice(0, 160)} />
      </Helmet>
      <Navbar />

      <ProgramSubNav
        slug={slug}
        title={tProgramTitle(t, slug, program.title)}
        subNavItems={resolveProgramSubNavItems(program)}
        program={program}
      />

      <section className="program-hero">
        {heroImage && (
          <picture className={`program-hero__bg${heroIsReady ? ' is-loaded is-instant' : ''}`}>
            {(heroWebp || heroWebpSm) && (
              <source
                type="image/webp"
                srcSet={[heroWebpSm && `${heroWebpSm} 960w`, heroWebp && `${heroWebp} 1600w`].filter(Boolean).join(', ')}
                sizes="100vw"
              />
            )}
            <img
              ref={heroImgRef}
              src={heroImage}
              alt=""
              fetchPriority="high"
              loading="eager"
              decoding={heroIsReady ? 'sync' : 'async'}
              onLoad={() => setHeroImageLoaded(true)}
              onError={() => setHeroImageLoaded(false)}
            />
          </picture>
        )}
        <div className="program-hero__overlay" />
        <div className="program-hero__content">
          <div className="program-hero__panel">
            <button
              type="button"
              className="program-hero__cta"
              onClick={() => {
                window.history.replaceState(null, '', `${programPath}#about`);
                scrollToProgramSection('about');
              }}
            >
              {(!program.hero_cta_text || program.hero_cta_text === 'التفاصيل')
                ? t('public.program.details')
                : program.hero_cta_text}
            </button>
            {(program.hero_tagline || slug === 'saqya') && (
              <p className="program-hero__tagline">
                {program.hero_tagline || t('public.program.saqyaTagline')}
              </p>
            )}
          </div>
        </div>
      </section>

      <section id="about" className={`program-section program-section--about${isKafala ? ' program-section--kafala-about' : ''}`}>
        <div className={`program-section__inner${video ? ' program-section__inner--about-media' : ''}`}>
          <div className="program-about__text">
            <h2 className="program-section__title">{tProgramTitle(t, slug, program.title)}</h2>
            <p className="program-section__text">{tProgramDescription(t, slug, program.description)}</p>
          </div>
          {video && (
            <div className={`program-about__video${video.type === 'instagram' ? ' program-about__video--instagram' : ''}${isFacebookVideoEmbed(video.src) ? ' program-about__video--facebook' : ''}`}>
              {video.type === 'file' ? (
                <video
                  className="program-about__player"
                  src={video.src}
                  controls
                  playsInline
                  preload="metadata"
                />
              ) : (
                <iframe
                  title={t('public.program.videoTitle', { title: tProgramTitle(t, slug, program.title) })}
                  src={sizedFacebookVideoSrc(video.src)}
                  className="program-about__iframe"
                  scrolling="no"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              )}
            </div>
          )}
        </div>
        {isKafala && <KafalaImpactStats />}
      </section>

      {isSaqya && <SaqyaWellRequest />}
      {isOmra && <OmraRequest />}

      {isKafala ? (
        <>
          <KafalaOrphelinsWaiting program={program} />
          <KafalaDemandeForm />
        </>
      ) : (
        <>
      <section id="current" className="program-section program-section--alt">
        <div className="program-section__inner">
          <h2 className="program-section__title">{t('public.program.currentProjects')}</h2>
          {projetsLoading ? (
            <p className="program-section__empty">{/* chargement discret */}…</p>
          ) : currentProjets.length > 0 ? (
            <div className="program-projets-grid">
              {currentProjets.map((p, index) => (
                <ProjetCard key={p.id} projet={p} priority={index < 3} />
              ))}
            </div>
          ) : (
            <p className="program-section__empty">{t('public.program.noCurrent')}</p>
          )}
        </div>
      </section>

      <section id="completed" className="program-section">
        <div className="program-section__inner">
          <h2 className="program-section__title">{t('public.program.completedProjects')}</h2>
          {projetsLoading ? (
            <p className="program-section__empty">…</p>
          ) : completedProjets.length > 0 ? (
            <div className="program-projets-grid">
              {completedProjets.map((p, index) => (
                <ProjetCard
                  key={p.id}
                  projet={p}
                  priority={currentProjets.length === 0 && index < 3}
                />
              ))}
            </div>
          ) : (
            <p className="program-section__empty">{t('public.program.noCompleted')}</p>
          )}
        </div>
      </section>
        </>
      )}

      <section id="donate" className="program-section program-section--donate">
        <div className="program-section__inner program-section__inner--center">
          <h2 className="program-section__title text-white">
            {(!program.donate_title || program.donate_title === 'تبرع للمشروع')
              ? t('public.program.donateTitle')
              : program.donate_title}
          </h2>
          <p className="program-section__text text-white/90 mb-8">
            {program.donate_text && !String(program.donate_text).includes('ساهم معنا في دعم برنامج')
              ? program.donate_text
              : t('public.program.donateText', { title: tProgramTitle(t, slug, program.title) })}
          </p>
          <Link
            to={`${paths.donate}?program=${slug}`}
            className="inline-flex items-center gap-2 bg-white text-brand-primary font-semibold py-3 px-8 rounded-full hover:bg-brand-surface transition-colors"
          >
            <FaDonate />
            {(!program.donate_button_text || program.donate_button_text === 'تبرع الآن')
              ? t('public.program.donateNow')
              : program.donate_button_text}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProgramDetail;
